<template>
    <div>
        <b-row class="my-2 align-items-center">
            <b-col cols="12" md="6" class="mb-2 mb-md-0">
                <b-input-group size="sm">
                    <b-form-input
                            v-model="filter"
                            type="search"
                            id="filterInput"
                            placeholder="Введите для поиска"
                    ></b-form-input>
                    <b-input-group-append>
                        <b-form-select
                                v-model="archiveOnly"
                                :options="archiveScopeOptions"
                                aria-label="Статус объекта"
                        ></b-form-select>
                    </b-input-group-append>
                </b-input-group>
            </b-col>
            <b-col cols="12" md="6">
                <div class="d-flex flex-wrap justify-content-md-end align-items-center">    
                    <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            align="fill"
                            size="sm"
                            class="my-0 mb-2 mb-md-0 mr-2"
                    ></b-pagination>
                    <div class="mb-2 mb-md-0">
                        <b-form-group
                                label="На странице"
                                label-cols="6"
                                label-size="sm"
                                label-for="perPageSelect"
                                class="mb-0 d-flex align-items-center"
                        >
                            <b-form-select
                                    v-model="perPage"
                                    id="perPageSelect"
                                    size="sm"
                                    :options="pageOptions"
                            ></b-form-select>
                        </b-form-group>
                    </div>
                </div>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="12" md="12" class="d-flex justify-content-end">
                <b-button
                            v-if="canCreateFuelLimit"
                            size="sm"
                            variant="success"
                            class="mb-2 mb-md-0 mr-2"
                            @click="openCreateModal"
                    >
                        Добавить лимит
                    </b-button>
            </b-col>
        </b-row>
        <b-alert
                v-if="errorMessage"
                variant="danger"
                show
                dismissible
                @dismissed="errorMessage = null"
                class="mb-2"
        >
            {{ errorMessage }}
        </b-alert>
        <b-table
                :fields="computedFields"
                striped
                small
                :items="items"
                :current-page="currentPage"
                :per-page="perPage"
                @filtered="onFiltered"
                :filter="filter"              
                :busy="isBusy"
                sticky-header="80vh"
        >
            <template v-if="canEditFuelLimits" #cell(actions)="row">
                <b-button
                        v-if="canEditFuelLimitRow(row.item)"
                        size="sm"
                        variant="outline-secondary"
                        class="mr-1 mb-1"
                        @click="openEditModal(row.item)"
                >
                    Редактировать
                </b-button>
                <b-button
                        v-if="canEditFuelLimitRow(row.item)"
                        size="sm"
                        variant="outline-danger"
                        class="mb-1"
                        @click="openDeleteModal(row.item)"
                >
                    Удалить
                </b-button>
            </template>
        </b-table>

        <b-modal
                id="fuel-limit-edit-modal"
                v-model="showEditModal"
                title="Редактирование лимита"
                @hide="resetForm"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :ok-disabled="!isFormValid || isSubmitting"
                :busy="isSubmitting"
                @ok.prevent="onSubmitEdit"
        >
            <b-form @submit.stop.prevent="onSubmitEdit">
                <b-form-group label="Объект">
                    <b-form-input
                            :value="currentLimitObjectName"
                            disabled
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Топливо" label-for="editFuel">
                    <b-form-select
                            id="editFuel"
                            v-model="form.fuel_id"
                            :options="fuelOptions"
                            disabled
                            required
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Лимит (единица измерения - литры!)" label-for="editValue">
                    <b-form-input
                            id="editValue"
                            v-model.number="form.value"
                            type="number"
                            min="0"
                            required
                    ></b-form-input>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                id="fuel-limit-create-modal"
                v-model="showCreateModal"
                title="Добавление лимита"
                @hide="resetForm"
                ok-title="Создать"
                cancel-title="Отмена"
                :ok-disabled="!isFormValidForCreate || isSubmitting"
                :busy="isSubmitting"
                @ok.prevent="onSubmitCreate"
        >
            <b-form @submit.stop.prevent="onSubmitCreate">
                <b-form-group label="Объект" label-for="createObject">
                    <v-select
                            id="createObject"
                            :options="objectOptions"
                            label="displayName"
                            :clearable="false"
                            :value="selectedObjectOption"
                            placeholder="Выберите объект"
                            @input="onSelectObject"
                    />
                </b-form-group>
                <b-form-group label="Топливо" label-for="createFuel">
                    <b-form-select
                            id="createFuel"
                            v-model="form.fuel_id"
                            :options="fuelOptions"
                            required
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Лимит (единица измерения - литры!)" label-for="createValue">
                   <div class="d-flex align-items-center">
                    <b-form-input
                            id="createValue"
                            v-model.number="form.value"
                            type="number"
                            min="0"
                            required
                    ></b-form-input>
                   </div>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                id="fuel-limit-delete-modal"
                v-model="showDeleteModal"
                title="Удаление лимитов по объекту"
                @hide="resetForm"
                ok-title="Удалить"
                cancel-title="Отмена"
                ok-variant="danger"
                :busy="isSubmitting"
                @ok.prevent="onConfirmDelete"
        >
            <p class="mb-0">
                Вы действительно хотите удалить <b>все</b> лимиты топлива по объекту
                <b>{{ currentLimitObjectName }}</b>?
            </p>
        </b-modal>
    </div>
</template>

<script>
    export default {
        name: "FuelLimits",
        computed: {
            items() {
                return Array.isArray(this.$store.state.fuelLimits)
                    ? this.$store.state.fuelLimits
                    : [];
            },
            canEditFuelLimits() {
                return !this.archiveOnly
                    && (this.hasFullFuelLimitAccess || this.curatedObjectProcessIds.length > 0);
            },
            canCreateFuelLimit() {
                return !this.archiveOnly
                    && (this.hasFullFuelLimitAccess || this.objectOptions.length > 0);
            },
            hasFullFuelLimitAccess() {
                return this.$can && this.$can(`update`, `fuelLimits`);
            },
            currentUserId() {
                const user = this.$store.getters.currentUser;
                return user && user.userId ? Number(user.userId) : null;
            },
            curatedObjectProcessIds() {
                if (!this.currentUserId) {
                    return [];
                }
                const processes = Array.isArray(this.$store.state.objectsInProcess)
                    ? this.$store.state.objectsInProcess
                    : [];
                return processes
                    .filter(process => Number(process.curator_id) === this.currentUserId)
                    .map(process => Number(process.building_process_id))
                    .filter(id => Number.isInteger(id) && id > 0);
            },
            computedFields() {
                if (this.canEditFuelLimits) {
                    return this.fields.concat([
                        {
                            key: `actions`,
                            label: `Действия`,
                            class: `text-nowrap`
                        }
                    ]);
                }
                return this.fields;
            },
            objectOptions() {
                const processes = Array.isArray(this.$store.state.objectsInProcess)
                    ? this.$store.state.objectsInProcess
                    : [];
                const editableProcesses = this.hasFullFuelLimitAccess
                    ? processes
                    : processes.filter(p => this.curatedObjectProcessIds.includes(Number(p.building_process_id)));
                const byProcessFromLimits = {};
                this.items.forEach(el => {
                    if (!byProcessFromLimits[el.building_process_id]) {
                        byProcessFromLimits[el.building_process_id] = el;
                    }
                });
                return editableProcesses.map(p => {
                    const fromLimits = byProcessFromLimits[p.building_process_id];
                    const name =
                        (p.bo && p.bo.name) ||
                        p.object_name ||
                        (fromLimits && fromLimits.object_name) ||
                        `Процесс ${p.building_process_id}`;
                    return Object.assign({}, p, {
                        displayName: name
                    });
                });
            },
            fuelOptions() {
                const list = this.$store.state.fuelTypes || [];
                const sorted = list.slice().sort((a, b) => {
                    const na = (a.name || ``).localeCompare(b.name || ``, `ru`);
                    return na;
                });
                const options = sorted.map(el => ({
                    value: el.id,
                    text: el.unit ? `${el.name} (${el.unit})` : el.name
                }));
                return [
                    {value: null, text: `Выберите топливо`},
                    ...options
                ];
            },
            isFormValid() {
                return this.form && this.form.value !== null && this.form.value !== `` && !isNaN(this.form.value);
            },
            isFormValidForCreate() {
                return this.isFormValid && this.form.building_process_id && this.form.fuel_id;
            },
            currentLimitObjectName() {
                return this.currentRow ? this.currentRow.object_name : ``;
            },
        },
        data() {
            return {
                fields: [
                    {
                        key: `object_name`,
                        label: `Имя объекта`,
                        sortable: true
                    },
                    {
                        key: `name`,
                        label: `Имя топлива`,
                        sortable: true
                    },
                    {
                        key: `value`,
                        label: `Лимит`,
                        sortable: true
                    },
                    {
                        key: `unit`,
                        label: `Единица измерения`,
                        sortable: true,
                        tdClass: 'd-none d-sm-table-cell',
                        thClass: 'd-none d-sm-table-cell'
                    }
                ],
                filter: null,
                perPage: 200,
                pageOptions: [5, 10, 15, 20, 100, 200],
                totalRows: 1,
                currentPage: 1,
                filterOn: [],
                archiveOnly: false,
                archiveScopeOptions: [
                    {value: false, text: `Действующие объекты`},
                    {value: true, text: `Архив объектов`}
                ],
                isBusy: true,
                showEditModal: false,
                showCreateModal: false,
                showDeleteModal: false,
                form: {
                    building_process_id: null,
                    fuel_id: null,
                    value: null
                },
                selectedObjectOption: null,
                currentRow: null,
                isSubmitting: false,
                errorMessage: null
            }
        },
        mounted() {
            this.$store.dispatch(`getTypesOfFuel`).catch(() => {});
            this.loadFuelLimits();
            this.$store.dispatch(`getObjectsInProcess`).catch(() => {
                // игнорируем, таблица лимитов все равно работает
            });
        },
        methods: {
            async loadFuelLimits() {
                this.isBusy = true;
                try {
                    await this.$store.dispatch(`getFuelLimits`, this.archiveOnly);
                    await this.$nextTick();
                    this.totalRows = this.items.length;
                    this.currentPage = 1;
                } finally {
                    this.isBusy = false;
                }
            },
            onFiltered(filteredItems) {
                this.totalRows = filteredItems.length;
                this.currentPage = 1;
            },
            resetForm() {
                this.form = {
                    building_process_id: null,
                    fuel_id: null,
                    value: null
                };
                this.currentRow = null;
                this.isSubmitting = false;
                this.errorMessage = null;
                this.selectedObjectOption = null;
            },
            openEditModal(row) {
                if (!this.canEditFuelLimitRow(row)) {
                    this.errorMessage = `Нет прав для изменения лимитов топлива по этому объекту`;
                    return;
                }
                this.currentRow = row;
                this.form = {
                    building_process_id: row.building_process_id,
                    fuel_id: row.fuel_id,
                    value: row.value
                };
                this.showEditModal = true;
            },
            openCreateModal() {
                this.resetForm();
                this.showCreateModal = true;
            },
            openDeleteModal(row) {
                if (!this.canEditFuelLimitRow(row)) {
                    this.errorMessage = `Нет прав для удаления лимитов топлива по этому объекту`;
                    return;
                }
                this.currentRow = row;
                this.showDeleteModal = true;
            },
            async onSubmitEdit() {
                if (!this.isFormValid || !this.currentRow) {
                    return;
                }
                this.isSubmitting = true;
                this.errorMessage = null;
                try {
                    await this.$store.dispatch(`updateFuelLimit`, {
                        building_process_id: this.form.building_process_id,
                        fuel_id: this.form.fuel_id,
                        value: this.form.value
                    });
                    this.showEditModal = false;
                } catch (e) {
                    this.errorMessage = e && e.response && e.response.data
                        ? e.response.data
                        : `Ошибка сохранения лимита`;
                } finally {
                    this.isSubmitting = false;
                }
            },
            async onSubmitCreate() {
                if (!this.isFormValidForCreate) {
                    return;
                }
                this.isSubmitting = true;
                this.errorMessage = null;
                try {
                    await this.$store.dispatch(`createFuelLimit`, {
                        building_process_id: this.form.building_process_id,
                        fuel_id: this.form.fuel_id,
                        value: this.form.value
                    });
                    this.showCreateModal = false;
                } catch (e) {
                    this.errorMessage = e && e.response && e.response.data
                        ? e.response.data
                        : `Ошибка создания лимита`;
                } finally {
                    this.isSubmitting = false;
                }
            },
            async onConfirmDelete() {
                if (!this.currentRow) {
                    return;
                }
                this.isSubmitting = true;
                this.errorMessage = null;
                try {
                    await this.$store.dispatch(`deleteFuelLimitsByProcess`, {
                        building_process_id: this.currentRow.building_process_id,
                        fuel_id: this.currentRow.fuel_id
                    });
                    this.showDeleteModal = false;
                } catch (e) {
                    this.errorMessage = e && e.response && e.response.data
                        ? e.response.data
                        : `Ошибка удаления лимитов`;
                } finally {
                    this.isSubmitting = false;
                }
            },
            onSelectObject(option) {
                this.selectedObjectOption = option;
                this.form.building_process_id = option ? option.building_process_id : null;
            },
            canEditFuelLimitRow(row) {
                if (this.hasFullFuelLimitAccess) {
                    return true;
                }
                if (!row) {
                    return false;
                }
                return this.curatedObjectProcessIds.includes(Number(row.building_process_id));
            }
        },
        watch: {
            archiveOnly() {
                this.loadFuelLimits();
            }
        }
    }
</script>

<style scoped>

</style>
